import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Pill, 
  AlertTriangle, 
  Calendar, 
  Clock, 
  Loader,
  Home,
  Shield,
  XCircle
} from 'lucide-react';
import { getPrescription } from '../services/firebaseService';
import type { PrescriptionAnalysis } from '../types';
import '../styles/PrescriptionView.css';

export const PrescriptionView: React.FC = () => {
  const { prescriptionId } = useParams<{ prescriptionId: string }>();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState<PrescriptionAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPrescription = async () => {
      if (!prescriptionId) {
        setError('Invalid prescription ID');
        setLoading(false);
        return;
      }

      try {
        const data = await getPrescription(prescriptionId);
        if (data) {
          setPrescription(data);
        } else {
          setError('Prescription not found');
        }
      } catch (err) {
        console.error('Error loading prescription:', err);
        setError('Failed to load prescription');
      } finally {
        setLoading(false);
      }
    };

    loadPrescription();
  }, [prescriptionId]);

  if (loading) {
    return (
      <div className="prescription-view">
        <div className="loading-container">
          <Loader className="spin" size={48} />
          <p>Loading prescription...</p>
        </div>
      </div>
    );
  }

  if (error || !prescription) {
    return (
      <div className="prescription-view">
        <motion.div 
          className="error-container"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <XCircle size={64} className="error-icon" />
          <h2>Prescription Not Found</h2>
          <p>{error || 'The prescription you are looking for does not exist or has been removed.'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            <Home size={20} />
            <span>Go Home</span>
          </button>
        </motion.div>
      </div>
    );
  }

  const getSafetyColor = (score: number) => {
    if (score >= 80) return 'var(--success)';
    if (score >= 60) return 'var(--warning)';
    return 'var(--danger)';
  };

  return (
    <div className="prescription-view">
      <div className="page-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
      </div>

      <div className="container">
        <motion.div 
          className="view-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="header-icon">
            <Pill size={32} />
          </div>
          <h1>Prescription Details</h1>
          <p>Scanned prescription analysis results</p>
        </motion.div>

        <div className="view-content">
          {/* Safety Score Card */}
          <motion.div 
            className="safety-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="safety-header">
              <Shield size={24} />
              <h2>Safety Score</h2>
            </div>
            <div className="safety-score-display">
              <div 
                className="score-circle"
                style={{ 
                  background: `conic-gradient(${getSafetyColor(prescription.safetyScore)} ${prescription.safetyScore * 3.6}deg, var(--gray-200) 0deg)` 
                }}
              >
                <div className="score-inner">
                  <span className="score-value">{prescription.safetyScore}%</span>
                </div>
              </div>
              <p className="safety-description">
                {prescription.safetyScore >= 80 && '✅ This prescription appears safe'}
                {prescription.safetyScore >= 60 && prescription.safetyScore < 80 && '⚠️ Use with caution'}
                {prescription.safetyScore < 60 && '❌ High risk - consult doctor'}
              </p>
            </div>
          </motion.div>

          {/* Medicines List */}
          <motion.div 
            className="medicines-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2>
              <Pill size={24} />
              Prescribed Medicines ({prescription.medicines.length})
            </h2>
            <div className="medicines-list">
              {prescription.medicines.map((medicine, index) => (
                <motion.div 
                  key={index}
                  className="medicine-card"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <div className="medicine-header">
                    <h3>{medicine.name}</h3>
                    {medicine.confidence && (
                      <span className="confidence-badge">
                        {medicine.confidence}% confidence
                      </span>
                    )}
                  </div>
                  <p className="generic-name">{medicine.genericName || medicine.generic_name || 'N/A'}</p>
                  <div className="medicine-details">
                    <div className="detail-item">
                      <span className="detail-label">Dosage:</span>
                      <span className="detail-value">{medicine.dosage}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Frequency:</span>
                      <span className="detail-value">{medicine.frequency}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Duration:</span>
                      <span className="detail-value">{medicine.duration}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Warnings */}
          {prescription.warnings && prescription.warnings.length > 0 && (
            <motion.div 
              className="warnings-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2>
                <AlertTriangle size={24} />
                Important Warnings
              </h2>
              <div className="warnings-list">
                {prescription.warnings.map((warning, index) => (
                  <div key={index} className="warning-item">
                    <AlertTriangle size={16} />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Timestamp */}
          <div className="prescription-footer">
            <div className="footer-item">
              <Calendar size={16} />
              <span>
                Generated on {prescription.createdAt 
                  ? new Date(prescription.createdAt.seconds * 1000).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
            <div className="footer-item">
              <Clock size={16} />
              <span>ID: {prescription.prescriptionId}</span>
            </div>
          </div>

          <motion.button 
            className="btn btn-secondary btn-large"
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Home size={20} />
            <span>Analyze Your Prescription</span>
          </motion.button>
        </div>
      </div>
    </div>
  );
};
